import * as Sentry from '@sentry/browser';
import Z from 'vendor/zuora';
import { convertObjectPropsToLowerCase } from '~/javascripts/lib/convert_utils';
import {
  ZUORA_ERROR_EVENT_KEY,
  ZUORA_CLIENT_SIDE_ERROR_EVENT_KEY,
  ZUORA_SUCCESS_EVENT_KEY,
} from '~/javascripts/purchase/constants';
import {
  INVALID_ZIP_CODE_CITY_ERROR_TYPE,
  INVALID_ZIP_CODE_STATE_ERROR_TYPE,
  INVALID_STREET_NAME_ERROR_TYPE,
  INCOMPLETE_OR_INVALID_ADDRESS_ERROR_TYPE,
  ADDRESS_NOT_GEOCODED_ERROR_TYPE,
} from '~/javascripts/purchase/error_dictionary';

export function dispatchZuoraSuccess(paymentMethodId) {
  window.dispatchEvent(
    new CustomEvent(ZUORA_SUCCESS_EVENT_KEY, {
      detail: {
        paymentMethodId,
      },
    }),
  );
}

export function dispatchZuoraError(message) {
  window.dispatchEvent(
    new CustomEvent(ZUORA_ERROR_EVENT_KEY, {
      detail: {
        errors: { message },
      },
    }),
  );
}

export function dispatchClientSideZuoraError(message, code) {
  window.dispatchEvent(
    new CustomEvent(ZUORA_CLIENT_SIDE_ERROR_EVENT_KEY, {
      detail: {
        errors: { message, code },
      },
    }),
  );
}

export function subscribeToZuoraSuccessEvent(callback) {
  window.addEventListener(ZUORA_SUCCESS_EVENT_KEY, callback);
}

export function unsubscribeFromZuoraSuccessEvent(callback) {
  window.removeEventListener(ZUORA_SUCCESS_EVENT_KEY, callback);
}

export function subscribeToZuoraClientSideErrorEvent(callback) {
  window.addEventListener(ZUORA_CLIENT_SIDE_ERROR_EVENT_KEY, callback);
}

export function unsubscribeFromZuoraClientSideErrorEvent(callback) {
  window.removeEventListener(ZUORA_CLIENT_SIDE_ERROR_EVENT_KEY, callback);
}

export function subscribeToZuoraErrorEvent(callback) {
  window.addEventListener(ZUORA_ERROR_EVENT_KEY, callback);
}

export function unsubscribeFromZuoraErrorEvent(callback) {
  window.removeEventListener(ZUORA_ERROR_EVENT_KEY, callback);
}

export function zuoraRenderCallback(response) {
  try {
    if (!response.success) {
      throw new Error(`Error loading Payment Form: ${response.errorMessage}`);
    }
  } catch (e) {
    Sentry.captureException(e);
  }
}

export function zuoraErrorMessageCallback(key, code, message) {
  const decodedMsg = decodeURIComponent(message);
  // Merge attribute with error code to create a unique identifier for the error handling system
  const parsedCode = parseInt(code, 10);

  dispatchClientSideZuoraError(decodedMsg, `[${key}:${parsedCode}]`);
  // Send error to Zuora Hosted Payment Method (hpm) page
  // https://knowledgecenter.zuora.com/Zuora_Payments/Payment_Page_and_Payment_Link/Payment_Pages_2.0/KA_Error_Handling_for_Payment_Pages_2.0/Customize_Error_Messages_for_Payment_Pages_2.0#Define_Custom_Error_Message_Handling_Function
  return Z.sendErrorMessageToHpm(key, decodedMsg);
}

/**
 * Receives an error message from Zuora and returns an extracted Stripe code if found, otherwise null.
 *
 * Zuora documentation:
 * https://knowledgecenter.zuora.com/Zuora_Payments/Payment_Page_and_Payment_Link/Payment_Pages_2.0/KA_Error_Handling_for_Payment_Pages_2.0
 * https://knowledgecenter.zuora.com/Billing/Billing_and_Payments/LA_Hosted_Payment_Pages/B_Payment_Pages_2.0/N_Error_Handling_for_Payment_Pages_2.0/Customize_Error_Messages_for_Payment_Pages_2.0#Define_Custom_Error_Message_Handling_Function
 * https://knowledgecenter.zuora.com/Billing/Billing_and_Payments/LA_Hosted_Payment_Pages/B_Payment_Pages_2.0/H_Integrate_Payment_Pages_2.0/A_Advanced_Integration_of_Payment_Pages_2.0#Customize_Error_Messages_in_Advanced_Integration
 *
 * The error string is prefixed with a Zuora error code followed by a message. Any Stripe (Gateway) error is prefixed with `[GatewayTransactionError]`.
 *
 * Examples of Zuora error codes:
 * [ReCaptcha_Validation_Failed]
 * [Attempt_Exceed_Limitation]
 * [ExceededMaxLength]
 * [GatewayTransactionError]
 *
 * Any Stripe error will contain an {error_type}, optionally followed by {decline_code}, which is followed by an {error_code} of the format:
 * "[{error_type}/({decline_code}/){error_code}]"
 *
 * Examples of Stripe error codes:
 * [card_error/invalid_cvc]
 * [card_error/authentication_required/authentication_required]
 * [invalid_request_error/setup_intent_authentication_failure]
 *
 * Stripe documentation:
 * https://docs.stripe.com/api/errors#errors-type
 * https://docs.stripe.com/declines/codes#stripe-decline-codes
 * https://docs.stripe.com/api/setup_attempts/object#setup_attempt_object-setup_error
 *
 * @param {String} message
 * @returns extracted Stripe error code, or null
 */
export function extractStripeErrorCode(message) {
  // Ensure we return lower case to match error dictionaries
  // Match the last part of the string wrapped in [] into a capturing group called `code`
  const result = message?.toLowerCase().match(/(?<code>\[[\w/]+])[^\]]*$/);
  if (!result?.groups?.code) {
    return null;
  }

  return result?.groups?.code;
}

/*
 Substrings of error messages from Zuora to avoid matching on double spaces in the error message which could be prone to changes
 Example full error message from Zuora:
 The Zuora GetTax call to Avalara returned the following error(s): Address not geocoded.(Address cannot be geocoded.)  The city could not be determined.(The city could not be found or determined from postal code.)
*/
const INVALID_ZIP_CODE_CITY_ERROR_FRAGMENT =
  'The city could not be found or determined from postal code';
const INVALID_ZIP_CODE_STATE_ERROR_FRAGMENT = 'Zip is not valid for the state.';
const INVALID_STREET_NAME_ERROR_FRAGMENT =
  'An exact street name match could not be found and phonetically matching the street name resulted in either no matches or matches to more than one street name';
const INCOMPLETE_OR_INVALID_ADDRESS_FRAGMENT = 'Address is incomplete or invalid';
const ADDRESS_NOT_GEOCODED_ERROR_FRAGMENT = 'GetTaxError - Address not geocoded.';

/*
  Dictionary to map error messages to error types to present friendly errors
*/
const ZUORA_ERROR_CODE_MAP = convertObjectPropsToLowerCase({
  [INVALID_ZIP_CODE_CITY_ERROR_FRAGMENT]: INVALID_ZIP_CODE_CITY_ERROR_TYPE,
  [INVALID_ZIP_CODE_STATE_ERROR_FRAGMENT]: INVALID_ZIP_CODE_STATE_ERROR_TYPE,
  [INVALID_STREET_NAME_ERROR_FRAGMENT]: INVALID_STREET_NAME_ERROR_TYPE,
  [INCOMPLETE_OR_INVALID_ADDRESS_FRAGMENT]: INCOMPLETE_OR_INVALID_ADDRESS_ERROR_TYPE,
  [ADDRESS_NOT_GEOCODED_ERROR_FRAGMENT]: ADDRESS_NOT_GEOCODED_ERROR_TYPE,
});

// Error codes from Zuora
const ZUORA_PREVIEW_INTEGRATION_ERROR_CODE = 58730099;
const ZUORA_PURCHASE_INTEGRATION_ERROR_CODE = 58560099;

/**
 * Receives the code from error response and returns true if its related to Zuora server encountering an error
 * when communicating with an external system like payment gateway or tax engine provider when previewing or
 * purchasing an order, otherwise returns false.
 *
 * Zuora documentation:
 * https://knowledgecenter.zuora.com/Zuora_Central_Platform/API/AA_REST_API/Resource_codes_for_Billing_and_Payments_REST_API/580000_-_589999
 * https://knowledgecenter.zuora.com/Zuora_Central_Platform/Tenant_Management/Data_Loader/Understanding_Errors
 *
 * @param {Number} code
 * @returns {boolean}
 */
function isZuoraIntegrationError(code) {
  return [ZUORA_PREVIEW_INTEGRATION_ERROR_CODE, ZUORA_PURCHASE_INTEGRATION_ERROR_CODE].includes(
    code,
  );
}

/**
 * Receives code and message from error response and returns an error code if the code is related
 * to Zuora integration error and the message is found in the dictionary, otherwise returns Undefined
 *
 * @param {Number} code
 * @param {String} message
 * @returns {String|Undefined}
 */
export function extractCustomZuoraErrorCode(code, message = '') {
  let extractedCode;

  if (isZuoraIntegrationError(code)) {
    const errorKey = Object.keys(ZUORA_ERROR_CODE_MAP).find((key) =>
      message.toLowerCase().includes(key),
    );
    extractedCode = ZUORA_ERROR_CODE_MAP[errorKey];
  }
  return extractedCode;
}
